import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { VALUE_STATUS, SOURCE_TYPE_LABELS } from "@/lib/strings";

const DISCLOSURES = ["B8", "B9", "B10", "C5", "C6", "C7"];
const STATUSES = ["DRAFT", "IMPORTED", "TO_VERIFY", "VERIFIED", "APPROVED", "ESTIMATED", "NOT_APPLICABLE"];

function formatValue(row) {
  if (row.boolean_value !== null && row.boolean_value !== undefined) {
    return row.boolean_value ? "Sì" : "No";
  }
  if (row.numeric_value !== null && row.numeric_value !== undefined) {
    return row.numeric_value.toLocaleString("it-IT");
  }
  if (Array.isArray(row.text_value)) {
    return JSON.stringify(row.text_value);
  }
  return row.text_value || "—";
}

export default function SocialReview() {
  const { companyId, period, role } = useTenant();
  const [rows, setRows] = useState([]);
  const [disclosure, setDisclosure] = useState("ALL");
  const [source, setSource] = useState("ALL");
  const [status, setStatus] = useState("ALL");

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const params = {};
      if (disclosure !== "ALL") params.disclosure = disclosure;
      if (source !== "ALL") params.source_type = source;
      if (status !== "ALL") params.status = status;
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/social/values`, { params });
      setRows(data);
    } catch (err) {
      toast.error("Errore caricamento revisione", { description: formatApiError(err) });
    }
  }, [companyId, period?.id, disclosure, source, status]);

  useEffect(() => { load(); }, [load]);

  if (!period) return null;

  return (
    <div className="space-y-6" data-testid="social-review-page">
      <div>
        <Link to="/social" className="inline-flex items-center gap-1 text-sm text-green-700 hover:underline" data-testid="back-to-social">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Social Engine
        </Link>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2 mt-1">
          <ListChecks className="w-7 h-7 text-sky-700" aria-hidden="true" />
          {role === "DATA_ENTRY" ? "Riepilogo dati Social" : "Revisione dati Social"}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Tutti i valori VSME Social (B8–C7) del periodo {period.year}, con stato, fonte e filtri.
          I valori C7 sono visibili solo ai ruoli autorizzati.
        </p>
      </div>

      <div className="flex flex-wrap gap-3" data-testid="review-filters">
        <Select value={disclosure} onValueChange={setDisclosure}>
          <SelectTrigger className="w-[170px]" data-testid="filter-disclosure"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tutte le disclosure</SelectItem>
            {DISCLOSURES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[200px]" data-testid="filter-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tutte le fonti</SelectItem>
            {Object.entries(SOURCE_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tutti gli stati</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{VALUE_STATUS[s]?.label || s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-3 md:p-0">
          <Table className="hidden md:table" data-testid="review-table">
            <TableHeader>
              <TableRow>
                <TableHead>Disclosure</TableHead>
                <TableHead>Datapoint</TableHead>
                <TableHead>Valore</TableHead>
                <TableHead>Qualità</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Fonte</TableHead>
                <TableHead>Aggiornato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8" data-testid="review-empty">
                    Nessun valore trovato con questi filtri.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const st = VALUE_STATUS[r.status];
                return (
                  <TableRow key={r.id} data-testid={`review-row-${r.datapoint_code}`}>
                    <TableCell><Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200">{r.disclosure_code}</Badge></TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-slate-900">{r.label}</p>
                      <p className="text-[11px] text-slate-400">{r.datapoint_code}</p>
                    </TableCell>
                    <TableCell className="font-medium" data-testid={`review-value-${r.datapoint_code}`}>
                      {formatValue(r)}
                      {r.unit ? ` ${r.unit}` : ""}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={r.validation_status === "INCONSISTENT"
                          ? "bg-red-50 text-red-800 border-red-200"
                          : r.validation_status === "WARNING"
                            ? "bg-amber-50 text-amber-800 border-amber-200"
                            : "bg-green-50 text-green-800 border-green-200"}
                        data-testid={`review-quality-${r.datapoint_code}`}
                        title={r.validation_issue?.message || ""}
                      >
                        {r.validation_status || "VALID"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={st?.className || ""}>{st?.label || r.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {SOURCE_TYPE_LABELS[r.source_type] || r.source_type}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.updated_at ? new Date(r.updated_at).toLocaleDateString("it-IT") : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="space-y-3 md:hidden" data-testid="review-mobile-list">
            {rows.length === 0 && (
              <p className="py-8 text-center text-sm text-slate-500" data-testid="review-mobile-empty">
                Nessun valore trovato con questi filtri.
              </p>
            )}
            {rows.map((row) => {
              const statusMeta = VALUE_STATUS[row.status];
              const qualityClass = row.validation_status === "INCONSISTENT"
                ? "bg-red-50 text-red-800 border-red-200"
                : row.validation_status === "WARNING"
                  ? "bg-amber-50 text-amber-800 border-amber-200"
                  : "bg-green-50 text-green-800 border-green-200";
              return (
                <div
                  key={row.id}
                  className="border border-slate-200 p-3"
                  data-testid={`review-mobile-row-${row.datapoint_code}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Badge
                        variant="outline"
                        className="bg-sky-50 text-sky-800 border-sky-200"
                      >
                        {row.disclosure_code}
                      </Badge>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{row.label}</p>
                      <p className="text-[11px] text-slate-400">{row.datapoint_code}</p>
                    </div>
                    <Badge variant="outline" className={qualityClass}>
                      {row.validation_status || "VALID"}
                    </Badge>
                  </div>
                  <p
                    className="mt-3 break-words text-base font-semibold text-slate-900"
                    data-testid={`review-mobile-value-${row.datapoint_code}`}
                  >
                    {formatValue(row)}{row.unit ? ` ${row.unit}` : ""}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    {statusMeta?.label || row.status} ·
                    {" "}{SOURCE_TYPE_LABELS[row.source_type] || row.source_type}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
